import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Modal } from '../layout';
import {Button as AugButton} from '../layout/buttons/Button';
import { Container, PageHeader } from '../layout';
import SimpleTable from '../layout/table/SimpleTable';
import AugAlert from '../layout/alert/AugAlert';
import { fetchInterviewsByCanvassId, fetchAllInterviewsByCanvassId, deleteInterview } from '../../utils/interviews';
import { Row, Col } from 'react-bootstrap';
import Icon from '../layout/icons/Icons';
import styles from './CanvassInterviews.module.css';

const CanvassInterviews = () => {
  const { canvassId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [interviews, setInterviews] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(undefined);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [exporting, setExporting] = useState(false);
  const manuallyClosedRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const result = await fetchInterviewsByCanvassId({ canvassId, page, pageSize, returnFull: true });
        if (isCancelled) return;
        setInterviews(Array.isArray(result.interviews) ? result.interviews : []);
        setTotalPages(result.totalPages || undefined);
      } catch (e) {
        if (!isCancelled) setError(e?.message || 'Failed to load interviews');
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };
    if (canvassId) load();
    return () => { isCancelled = true; };
  }, [canvassId, page, pageSize]);

  // Auto-open modal if sessionId is in URL
  useEffect(() => {
    const sessionIdParam = searchParams.get('sessionId');
    if (sessionIdParam && interviews.length > 0 && !manuallyClosedRef.current) {
      const interview = interviews.find(int => int.metadata?.id === sessionIdParam || int.metadata?.sessionId === sessionIdParam);
      if (interview && !showSidePanel) {
        manuallyClosedRef.current = false;
        setSelectedInterview(interview);
        setShowSidePanel(true);
      }
    }
    // Reset ref when sessionId changes (user navigated with new URL)
    if (sessionIdParam) {
      manuallyClosedRef.current = false;
    }
  }, [searchParams, interviews, showSidePanel]);

  const onPageChange = (p) => {
    if (loading) return;
    if (p < 1) return;
    if (totalPages && p > totalPages) return;
    setPage(p);
  };

  const hasNext = totalPages ? page < totalPages : interviews.length === pageSize;

  const openInterviewDetails = (interview) => {
    setSelectedInterview(interview);
    setShowSidePanel(true);
    manuallyClosedRef.current = false;
    // Update URL with sessionId for shareability
    const sessionId = interview.metadata?.id || interview.metadata?.sessionId;
    if (sessionId) {
      setSearchParams({ sessionId });
    }
  };

  const closeSidePanel = () => {
    setShowSidePanel(false);
    manuallyClosedRef.current = true;
    // Clear sessionId parameter from URL immediately
    setSearchParams({});
    setTimeout(() => setSelectedInterview(null), 300); // Clear after animation
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setShowDeleteConfirm(false);
    setDeleteError('');
  };

  const formatEmotionStrength = (strength) => {
    if (strength === null || strength === undefined) return '—';
    const numStrength = typeof strength === 'string' ? parseFloat(strength) : strength;
    if (isNaN(numStrength)) return '—';
    return numStrength.toFixed(2);
  };

  const getEmotionStrengthStyle = (strength) => {
    if (strength === null || strength === undefined) return {};
    const numStrength = typeof strength === 'string' ? parseFloat(strength) : strength;
    if (isNaN(numStrength)) return {};
    
    // Scale font size from 0.75em (0.0) to 1.25em (1.0)
    const fontSize = 0.75 + (numStrength * 0.5);
    
    // Scale opacity from 0.4 (0.0) to 1.0 (1.0)
    const opacity = 0.4 + (numStrength * 0.6);
    
    // Color gradient from green (low) through yellow (medium) to red (high)
    let color;
    if (numStrength >= 0.8) {
      color = 'var(--brand-as-red-700, #b91c1c)';
    } else if (numStrength >= 0.5) {
      color = 'var(--brand-as-yellow-700, #a16207)';
    } else {
      color = 'var(--brand-as-green-700, #15803d)';
    }
    
    // Background color with varying intensity
    let backgroundColor;
    if (numStrength >= 0.8) {
      backgroundColor = 'var(--brand-as-red-50, #fef2f2)';
    } else if (numStrength >= 0.5) {
      backgroundColor = 'var(--brand-as-yellow-50, #fffbeb)';
    } else {
      backgroundColor = 'var(--brand-as-green-50, #f0fdf4)';
    }
    
    return {
      fontSize: `${fontSize}em`,
      opacity,
      color,
      backgroundColor,
      fontWeight: numStrength >= 0.7 ? '600' : numStrength >= 0.4 ? '500' : '400',
      textAlign: 'center',
      padding: '2px 6px',
      borderRadius: '4px',
      border: '1px solid var(--brand-as-neutral-200, #e5e7eb)'
    };
  };

  const confirmDelete = async () => {
    const interviewId = selectedInterview?.metadata?.id;
    if (!interviewId) {
      setDeleteError('Missing interview id');
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError('');
      await deleteInterview({ interviewId });

      setInterviews((prev) => (prev || []).filter((it) => it?.metadata?.id !== interviewId));
      setShowDeleteConfirm(false);
      closeSidePanel();
    } catch (e) {
      setDeleteError(e?.message || 'Failed to delete interview');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Export a single interview to CSV (includes canvassId)
  const downloadCSV = (interview) => {
    if (!interview) return;
    const meta = interview.metadata || {};
    const msgs = interview.messages || [];
    const baseHeaders = ['Canvass ID', 'Session ID', 'State', 'Questions Completed', 'Last Activity', 'Message Count'];
    const messageHeaders = msgs.map((_, i) => `Message ${i + 1}`);
    const headers = [...baseHeaders, ...messageHeaders];

    const baseRow = [
      canvassId,
      meta.sessionId || '—',
      meta.state || '—',
      meta.metadata?.questionsCompleted ?? '—',
      meta.metadata?.lastActivity ? new Date(meta.metadata.lastActivity).toLocaleString() : '—',
      msgs.length,
    ];
    const messageCells = msgs.map(m => JSON.stringify(m));
    const row = [...baseRow, ...messageCells];

    const rows = [headers, row];
    const csvContent = rows
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interview_${meta.sessionId || canvassId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = async () => {
    if (!interviews.length) return;
    try {
      setExporting(true);
      setError('');
      // Fetch ALL interviews for the canvass, not just the current page
      const allInterviews = await fetchAllInterviewsByCanvassId({ canvassId });
      const exportData = Array.isArray(allInterviews) && allInterviews.length > 0 ? allInterviews : interviews;

      const baseHeaders = ['Canvass ID', 'Session ID', 'State', 'Questions Completed', 'Last Activity', 'Message Count'];
      const maxMsgs = Math.max(0, ...exportData.map(it => (it.messages || []).length));
      const messageHeaders = Array.from({ length: maxMsgs }, (_, i) => `Message ${i + 1}`);
      const headers = [...baseHeaders, ...messageHeaders];

      const rows = [
        headers,
        ...exportData.map(it => {
          const meta = it.metadata || {};
          const msgs = it.messages || [];
          const baseRow = [
            canvassId,
            meta.sessionId || '—',
            meta.state || '—',
            meta.metadata?.questionsCompleted ?? '—',
            meta.metadata?.lastActivity ? new Date(meta.metadata.lastActivity).toLocaleString() : '—',
            msgs.length,
          ];
          const messageCells = msgs.map(m => JSON.stringify(m));
          while (messageCells.length < maxMsgs) messageCells.push('');
          return [...baseRow, ...messageCells];
        })
      ];
      const csvContent = rows
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `interviews_${canvassId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Failed to export interviews');
    } finally {
      setExporting(false);
    }
  };

  const renderRows = () => {
    const loadingRow = loading ? (
      <tr key="loading">
        <td colSpan={5} className="text-center text-muted">Loading interviews...</td>
      </tr>
    ) : null;

    if (!interviews.length) {
      return (
        loadingRow || (
          <tr>
            <td colSpan={5} className="text-center text-muted">No interviews found</td>
          </tr>
        )
      );
    }

    const interviewRows = interviews.map((it, idx) => {
      const meta = it.metadata || {};
      const msgs = it.messages || [];
      return (
        <tr key={meta.sessionId || idx} onClick={() => openInterviewDetails(it)} style={{ cursor: 'pointer' }}>
          <td>{meta.sessionId || '—'}</td>
          <td>{meta.state || '—'}</td>
          <td>{meta.metadata?.questionsCompleted ?? '—'}</td>
          <td>{meta.metadata?.lastActivity ? new Date(meta.metadata.lastActivity).toLocaleString() : '—'}</td>
          <td>{msgs.length}</td>
        </tr>
      );
    });

    return loadingRow ? [loadingRow, ...interviewRows] : interviewRows;
  };

  const renderMobileCards = () => {
    if (loading) {
      return <div className={styles.mobileEmptyState}>Loading interviews...</div>;
    }

    if (!interviews.length) {
      return <div className={styles.mobileEmptyState}>No interviews found</div>;
    }

    return interviews.map((it, idx) => {
      const meta = it.metadata || {};
      const msgs = it.messages || [];

      return (
        <div
          key={meta.sessionId || idx}
          className={styles.mobileInterviewCard}
          role="button"
          tabIndex={0}
          onClick={() => openInterviewDetails(it)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openInterviewDetails(it);
            }
          }}
        >
          <div className={styles.mobileCardHeader}>
            <div className={styles.mobileCardTitleBlock}>
              <div className={styles.mobileCardTitle}>{meta.sessionId || '—'}</div>
              <div className={styles.mobileCardSubtext}>{meta.state || '—'}</div>
            </div>
          </div>

          <div className={styles.mobileCardDetails}>
            <div className={styles.mobileDetailItem}>
              <span className={styles.mobileDetailLabel}>Questions Completed</span>
              <span className={styles.mobileDetailValue}>{meta.metadata?.questionsCompleted ?? '—'}</span>
            </div>
            <div className={styles.mobileDetailItem}>
              <span className={styles.mobileDetailLabel}>Last Activity</span>
              <span className={styles.mobileDetailValue}>{meta.metadata?.lastActivity ? new Date(meta.metadata.lastActivity).toLocaleDateString() : '—'}</span>
            </div>
            <div className={styles.mobileDetailItem}>
              <span className={styles.mobileDetailLabel}>Message Count</span>
              <span className={styles.mobileDetailValue}>{msgs.length}</span>
            </div>
          </div>

          <div className={styles.mobileCardActions}>
            <button
              type="button"
              className={styles.mobileActionButton}
              onClick={(e) => {
                e.stopPropagation();
                openInterviewDetails(it);
              }}
            >
              View Details
            </button>
          </div>
        </div>
      );
    });
  };

  return (
    <Container fluid>
      <Row>
        <Col className="d-flex justify-content-start">
          <a className={styles.backLink} href="/canvasses" aria-label="Back to Canvasses">
            <Icon className={styles.backIcon} name="arrow/arrow_back" color="var(--brand-as-blue-600)" width={22} height={22} />
            <div className={styles.backText}>Back to Canvasses</div>
          </a>
        </Col>
      </Row>
      <Row>
        <Col>
          <PageHeader title="Interviews" subtext={`Canvass: ${canvassId}`} />
        </Col>
        <Col md={3} className="d-flex justify-content-end align-items-center gap-2">
          <AugButton 
            onClick={exportToCSV}
            className="export-btn"
            disabled={!interviews.length || exporting}
            title={interviews.length ? 'Export all interviews to CSV' : 'No interviews to export'}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </AugButton>
        </Col>
      </Row>
      {error && <AugAlert type="error" title="Error" text={error} className="mb-4" />}
      <Container className={styles.dataContainer}>
        <div className={styles.desktopTableWrap}>
          <SimpleTable
              className="interviews-table"
              headers={["Session ID", "State", "Questions Completed", "Last Activity", "Message Count"]}
              pagination={{
              page,
              totalPages,
              hasNext,
              loading,
              onPageChange,
              }}
          >
              {renderRows()}
          </SimpleTable>
        </div>

        <div className={styles.mobileCardsWrap}>
          {renderMobileCards()}
        </div>

        <div className={styles.mobilePaginationWrap}>
          <div className={styles.mobilePaginationInfo}>Page {page}{totalPages ? ` of ${totalPages}` : ''}</div>
          <div className={styles.mobilePaginationActions}>
            <button
              type="button"
              className={styles.mobilePaginationButton}
              onClick={() => onPageChange(page - 1)}
              disabled={loading || page <= 1}
            >
              Previous
            </button>
            <button
              type="button"
              className={styles.mobilePaginationButton}
              onClick={() => onPageChange(page + 1)}
              disabled={loading || !hasNext}
            >
              Next
            </button>
          </div>
        </div>
      </Container>
      {showSidePanel && selectedInterview && (
        <Modal
          open={showSidePanel}
          onClose={closeSidePanel}
          title="Interview Details"
        >
          <div className={styles.interviewModalContent}>
          <div className={`mb-3 ${styles.interviewModalHeader}`}>
            <p className="mb-0">
              Session ID: {selectedInterview.metadata?.sessionId || '—'}
              {selectedInterview.metadata?.metadata?.lastActivity ? ` - Last Activity: ${new Date(selectedInterview.metadata.metadata.lastActivity).toLocaleString()}` : ''}
            </p>
          </div>

          <div className={`${styles.interviewModalBody} side-panel-body`}>
            {/* Interview Information */}
            <div className={`${styles.interviewModalSection} user-info-section`}>
              <h6>Interview Information</h6>
              <div className={`${styles.interviewInfoGrid} info-grid`}>
                <div className={`${styles.interviewInfoItem} info-item`}>
                  <span className="info-label">Session ID:</span>
                  <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata?.sessionId || '—'}</span>
                </div>
                <div className={`${styles.interviewInfoItem} info-item`}>
                  <span className="info-label">State:</span>
                  <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata?.state || '—'}</span>
                </div>
                <div className={`${styles.interviewInfoItem} info-item`}>
                  <span className="info-label">Questions Completed:</span>
                  <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata?.metadata?.questionsCompleted ?? '—'}</span>
                </div>
                <div className={`${styles.interviewInfoItem} info-item`}>
                  <span className="info-label">Last Activity:</span>
                  <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata?.metadata?.lastActivity ? new Date(selectedInterview.metadata.metadata.lastActivity).toLocaleString() : '—'}</span>
                </div>
                <div className={`${styles.interviewInfoItem} info-item`}>
                  <span className="info-label">Message Count:</span>
                  <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.messages?.length || 0}</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className={`${styles.interviewModalSection} messages-section`}>
              <h6>Messages</h6>
              <div className={`${styles.interviewThreadContainer} conversation-thread-container`}>
                {selectedInterview.messages && selectedInterview.messages.length > 0 ? (
                  selectedInterview.messages.map((msg, idx) => {
                    // Find corresponding emotional journey data for user messages
                    // Count user messages before this index to get the correct turn number
                    const userMessageCount = selectedInterview.messages
                      .slice(0, idx + 1)
                      .filter(m => m.role === 'user').length;
                    
                    const emotionalJourneyData = msg.role === 'user' && selectedInterview.insights && selectedInterview.insights.emotional_journey && selectedInterview.insights.emotional_journey.by_turn && selectedInterview.insights.emotional_journey.by_turn.find(
                      (journey, journeyIdx) => journey.turn === userMessageCount
                    );
                    
                    return (
                      <div key={idx} className={`${styles.interviewMessageWrapper} message-wrapper ${msg.role === 'user' ? 'user' : 'assistant'}`}>
                        <div className={`${styles.interviewMessageContent} message-content`}>
                          <div className="mb-2">
                            <strong className={msg.role === 'user' ? 'text-success' : 'text-primary'}>
                              {msg.role || `Message ${idx + 1}`}
                            </strong>
                          </div>
                          {msg.role === 'user' && emotionalJourneyData ? (
                            <div className={`${styles.interviewMessageBubble} message-bubble user-emotions`}>
                              {/* User content */}
                              <div className="mb-3">
                                {msg.content || 'No content'}
                              </div>
                              
                              {/* Key Emotions */}
                              {emotionalJourneyData.key_emotions && (
                                <div className="mb-2">
                                  <div className="mb-1">
                                    <strong>Key Emotions:</strong>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    {emotionalJourneyData.key_emotions.map((emotion, emotionIdx) => (
                                      <span 
                                        key={emotionIdx}
                                        className="badge"
                                        style={{
                                          backgroundColor: `rgba(59, 130, 246, ${emotion.intensity / 10})`,
                                          color: emotion.intensity > 5 ? 'white' : '#333',
                                          fontSize: `${0.8 + (emotion.intensity / 20)}em`,
                                          fontWeight: emotion.intensity > 7 ? '600' : '500',
                                          padding: '4px 8px',
                                          border: '1px solid rgba(59, 130, 246, 0.3)'
                                        }}
                                      >
                                        {emotion.label} ({emotion.intensity})
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Emotional Summary */}
                              {emotionalJourneyData.emotional_summary && (
                                <div className="mt-2 pt-2 border-top">
                                  <div className="mb-1">
                                    <strong>Emotional Summary:</strong>
                                  </div>
                                  <div className="text-muted" style={{ fontSize: '0.9em', fontStyle: 'italic' }}>
                                    {emotionalJourneyData.emotional_summary}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className={`${styles.interviewMessageBubble} message-bubble ${msg.role === 'user' ? 'user' : 'assistant'}`}>
                              {typeof msg.content === 'object' ? msg.content.question : (msg.content || 'No content')}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted">No messages recorded.</p>
                )}
              </div>
            </div>

            {/* Interview Analysis */}
            {selectedInterview.metadata?.interviewAnalysis && (
              <div className={`${styles.interviewModalSection} user-info-section`}>
                <h6>Interview Analysis</h6>
                <div className={`${styles.interviewInfoGrid} info-grid`}>
                  <div className={`${styles.interviewInfoItem} info-item`}>
                    <span className="info-label">Participant ID:</span>
                    <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata.interviewAnalysis.participant_id || '—'}</span>
                  </div>
                  <div className={`${styles.interviewInfoItem} info-item`}>
                    <span className="info-label">Psychographic Archetype:</span>
                    <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata.interviewAnalysis.psychographic_archetype || '—'}</span>
                  </div>
                  <div className={`${styles.interviewInfoItem} info-item`}>
                    <span className="info-label">Dominant Emotion:</span>
                    <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.metadata.interviewAnalysis.dominant_emotion || '—'}</span>
                  </div>
                </div>
                
                {selectedInterview.metadata.interviewAnalysis.narrative_summary && (
                  <div className="mt-3">
                    <h6 className="text-muted">Narrative Summary</h6>
                    <p className="text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {selectedInterview.metadata.interviewAnalysis.narrative_summary}
                    </p>
                  </div>
                )}

                {selectedInterview.metadata.interviewAnalysis.emotions && (
                  <div className="mt-3">
                    <h6 className="text-muted">Emotions</h6>
                    <div className={`${styles.interviewInfoGrid} info-grid`}>
                      {selectedInterview.metadata.interviewAnalysis.emotions.map((emotion, index) => (
                        <div key={index} className={`${styles.interviewInfoItem} info-item`}>
                          <span className="info-label">{emotion.emotion || '—'}:</span>
                          <span 
                            className={`${styles.interviewInfoValue} info-value emotion-strength-value`}
                            style={getEmotionStrengthStyle(emotion.strength)}
                          >
                            {formatEmotionStrength(emotion.strength)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Interview Insights */}
            {selectedInterview.insights && Object.keys(selectedInterview.insights).length > 0 && (
              <div className={`${styles.interviewModalSection} user-info-section`}>
                <h6>Interview Insights</h6>
                
                {selectedInterview.insights.participant_profile && (
                  <div className="mb-4">
                    <h6 className="text-muted">Participant Profile</h6>
                    <div className={`${styles.interviewInfoGrid} info-grid`}>
                      <div className={`${styles.interviewInfoItem} info-item`}>
                        <span className="info-label">Role:</span>
                        <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.insights.participant_profile.role || '—'}</span>
                      </div>
                      <div className={`${styles.interviewInfoItem} info-item`}>
                        <span className="info-label">Context:</span>
                        <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.insights.participant_profile.context || '—'}</span>
                      </div>
                    </div>
                    
                    {selectedInterview.insights.participant_profile.primary_needs && Array.isArray(selectedInterview.insights.participant_profile.primary_needs) && selectedInterview.insights.participant_profile.primary_needs.length > 0 && (
                      <div className="mt-3">
                        <h6 className="text-muted">Primary Needs</h6>
                        <div style={{ width: '100%' }}>
                          <div style={{ padding: '12px', borderRadius: '6px', border: '1px solid var(--brand-as-neutral-200, #e5e7eb)' }}>
                            <ul style={{ margin: '0', paddingLeft: '20px', textAlign: 'left' }}>
                              {selectedInterview.insights.participant_profile.primary_needs.map((need, index) => (
                                <li key={index} style={{ marginBottom: '4px', color: 'var(--brand-as-neutral-900, #111827)' }}>{need || '—'}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedInterview.insights.participant_profile.persona_inferred && (
                      <div className="mt-3">
                        <h6 className="text-muted">Inferred Persona</h6>
                        <div className={`${styles.interviewInfoGrid} info-grid`}>
                          <div className={`${styles.interviewInfoItem} info-item`}>
                            <span className="info-label">Age Cohort:</span>
                            <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.insights.participant_profile.persona_inferred.age_cohort || '—'}</span>
                          </div>
                          <div className={`${styles.interviewInfoItem} info-item`}>
                            <span className="info-label">Style Orientation:</span>
                            <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.insights.participant_profile.persona_inferred.style_orientation || '—'}</span>
                          </div>
                          <div className={`${styles.interviewInfoItem} info-item`}>
                            <span className="info-label">Decision Style:</span>
                            <span className={`${styles.interviewInfoValue} info-value`}>{selectedInterview.insights.participant_profile.persona_inferred.decision_style || '—'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedInterview.insights.core_themes && Array.isArray(selectedInterview.insights.core_themes) && selectedInterview.insights.core_themes.length > 0 && (
                  <div>
                    <h6 className="text-muted">Core Themes</h6>
                    {selectedInterview.insights.core_themes.map((theme, index) => (
                      <div key={index} className="mb-3 p-3 border rounded">
                        <h5 className="mb-2">{theme.label}</h5>
                        <div className="mb-2" style={{ textAlign: 'left' }}>
                          <strong>Emotional Impact:</strong> <span className="text-muted">{theme.emotional_impact}</span>
                        </div>
                        {theme.business_risk && (
                          <div className="mb-2" style={{ textAlign: 'left' }}>
                            <strong>Business Risk:</strong> <span className="text-muted">{theme.business_risk}</span>
                          </div>
                        )}
                        {theme.business_opportunity && (
                          <div style={{ textAlign: 'left' }}>
                            <strong>Business Opportunity:</strong> <span className="text-muted">{theme.business_opportunity}</span>
                          </div>
                        )}
                        <div className="mb-2" style={{ textAlign: 'left' }}>
                          <strong>Evidence:</strong>
                          <ul className="mb-2">
                            {theme.evidence?.map((evidence, i) => (
                              <li key={i} style={{ fontSize: '0.875em', textAlign: 'left' }}>{evidence}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actionable Recommendations */}
            {selectedInterview.insights && selectedInterview.insights.actionable_recommendations && Array.isArray(selectedInterview.insights.actionable_recommendations) && selectedInterview.insights.actionable_recommendations.length > 0 && (
              <div className={`${styles.interviewModalSection} user-info-section`}>
                <h6>Actionable Recommendations</h6>
                {selectedInterview.insights.actionable_recommendations.map((recommendation, index) => (
                  <div key={index} className="mb-4 p-3 border rounded" style={{ textAlign: 'left' }}>
                    <div className="mb-2">
                      <span className={`badge me-2 ${
                        recommendation.priority === 'critical' ? 'bg-danger' :
                        recommendation.priority === 'very_high' ? 'bg-danger' :
                        recommendation.priority === 'high' ? 'bg-warning' :
                        recommendation.priority === 'medium' ? 'bg-info' :
                        recommendation.priority === 'low' ? 'bg-success' : 'bg-secondary'
                      }`}>
                        {recommendation.priority || 'medium'}
                      </span>
                      <h6 className="d-inline-block mb-0">{recommendation.title}</h6>
                    </div>
                    <div className="mb-3">
                      <p className="mb-0 text-muted">{recommendation.description}</p>
                    </div>
                    {recommendation.specific_suggestions && (
                      <div className="mb-3">
                        <strong>Specific Suggestions:</strong>
                        <ul className="mb-0 mt-2">
                          {recommendation.specific_suggestions.map((suggestion, i) => (
                            <li key={i} style={{ fontSize: '0.875em', textAlign: 'left' }}>{suggestion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {recommendation.expected_emotional_outcome && (
                      <div className="mb-2">
                        <strong>Expected Emotional Outcome:</strong> <span className="text-muted">{recommendation.expected_emotional_outcome}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Download CSV */}
            <div className={`${styles.interviewModalSection} conversation-stats-section`}>
              <h6>Actions</h6>
              <div className={styles.interviewActionPanel}>
                <AugButton
                  onClick={() => downloadCSV(selectedInterview)}
                  style={{
                    width: '100%',
                    background: 'white',
                    border: '2px solid #000',
                    color: 'black',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = '#f8f9fa'; }}
                  onMouseLeave={(e) => { e.target.style.background = 'white'; }}
                >
                  Download CSV
                </AugButton>

                <AugButton
                  onClick={() => { setShowDeleteConfirm(true); setDeleteError(''); }}
                  variant="secondary"
                  style={{
                    width: '100%',
                    marginTop: '10px',
                    background: 'var(--brand-as-purple-800)',
                    border: '2px solid var(--brand-as-purple-800)',
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '500',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Delete Interview
                </AugButton>
              </div>
            </div>
          </div>
          </div>
        </Modal>
      )}

      {showDeleteConfirm && selectedInterview && (
        <Modal
          open={showDeleteConfirm}
          onClose={closeDeleteConfirm}
          title="Confirm Delete"
          actions={
            <>
              <AugButton variant="primary"
                onClick={closeDeleteConfirm}
                disabled={deleteLoading}
                style={{
                  background: 'white',
                  border: '2px solid #000',
                  color: 'black',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </AugButton>
              <AugButton variant="primary"
                onClick={confirmDelete}
                disabled={deleteLoading}
                style={{
                  background: 'var(--brand-as-purple-800)',
                  border: '2px solid var(--brand-as-purple-800)',
                  color: 'white',transition: 'all 0.3s ease'
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
              </AugButton>
            </>
          }
        >
          <p className="mb-2">
            This will mark the interview as deleted. You can’t undo this action.
          </p>
          <p className="mb-3 text-muted">
            Session ID: {selectedInterview.metadata?.sessionId || '—'}
            <br />
            Questions Answered: {selectedInterview.metadata?.metadata?.questionsCompleted || '—'}
            <br />
            Status: {selectedInterview.metadata?.state || '—'}
            <br />
            Last Activity: {selectedInterview.metadata?.metadata?.lastActivity ? new Date(selectedInterview.metadata.metadata.lastActivity).toLocaleString() : '—'}
          </p>
          {deleteError && <AugAlert type="error" title="Delete Error" text={deleteError} />}
        </Modal>
      )}
    </Container>
  );
};

export default CanvassInterviews;
